import React, { useState } from 'react';
import Icon from 'components/AppIcon';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { useAIAgent } from '@/contexts/AIAgentContext';
import { PulseBeamBorder } from '@/components/ui/PulseBeamBorder';

const JobApplicationForm = ({ job, onClose }) => {
  const {
    agentState,
    isActive,
    updateAgentState,
    addLog,
    setPendingInput,
    setAgentMessage,
    currentJob,
    pendingInput
  } = useAIAgent();

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    resume: null,
    coverLetter: '',
    linkedinUrl: '',
    portfolioUrl: '',
    answers: {}
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [resumeFile, setResumeFile] = useState(null);
  const [errors, setErrors] = useState({});
  const [isMinimized, setIsMinimized] = useState(false); // New state for minimization

  const totalSteps = 3;

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const handleAnswerChange = (questionId, value) => {
    setFormData(prev => ({
      ...prev,
      answers: {
        ...prev.answers,
        [questionId]: value
      }
    }));
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        setErrors(prev => ({
          ...prev,
          resume: 'File size must be less than 5MB'
        }));
        return;
      }

      const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      if (!allowedTypes.includes(file.type)) {
        setErrors(prev => ({
          ...prev,
          resume: 'Please upload a PDF or Word document'
        }));
        return;
      }

      setFormData(prev => ({
        ...prev,
        resume: { name: file.name } // Just store metadata
      }));
      setResumeFile(file); // Store actual file separately
      setErrors(prev => ({
        ...prev,
        resume: null
      }));
    }
  };

  const validateStep = (step) => {
    const newErrors = {};

    if (step === 1) {
      if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
      if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
      if (!formData.email.trim()) {
        newErrors.email = 'Email is required';
      } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
        newErrors.email = 'Please enter a valid email';
      }
      if (!formData.phone.trim()) newErrors.phone = 'Phone number is required';
    }

    if (step === 2) {
      if (!formData.resume) newErrors.resume = 'Resume is required';
      if (!formData.coverLetter.trim()) newErrors.coverLetter = 'Cover letter is required';
    }

    if (step === 3) {
      job.applicationQuestions.forEach(question => {
        if (question.required && !formData.answers[question.id]) {
          newErrors[`question_${question.id}`] = 'This field is required';
        }
      });
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, totalSteps));
    }
  };

  const handlePrevious = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const { user } = useAuth();

  // --- AI AGENT LOGIC ---

  // 1. Analysis Phase (Instant)
  React.useEffect(() => {
    if (isActive && agentState === 'analyzing') {
      // Fast-track analysis
      addLog("Analyzed application form structure.", 'success');
      updateAgentState('filling', "Auto-filling your personal details...");
      setIsMinimized(true); // Auto-minimize when work starts
    }
  }, [isActive, agentState, updateAgentState, addLog]);

  // Auto-maximize on Review
  React.useEffect(() => {
    if (agentState === 'review') {
      setIsMinimized(false);
    }
  }, [agentState]);

  // 2. Auto-fill Phase (Silent & Fast)
  React.useEffect(() => {
    if (isActive && agentState === 'filling') {
      const fillData = async () => {
        // Fill Personal Info Instantly
        setFormData(prev => ({
          ...prev,
          firstName: user?.user_metadata?.first_name || 'Alex',
          lastName: user?.user_metadata?.last_name || 'Chen',
          email: user?.email || '',
          phone: '555-0123',
          linkedinUrl: 'https://linkedin.com/in/alexc',
          portfolioUrl: 'https://alexchen.dev',
          resume: { name: 'Existing_Resume_2024.pdf' }
        }));

        // Just one log for all personal data
        addLog("✅ Personal information & Resume attached.", 'success');

        await new Promise(r => setTimeout(r, 800)); // Small distinct pause
        setCurrentStep(2);

        // Move to Input Phase - WITH SMART SUGGESTIONS
        updateAgentState('input_needed', "I've analyzed the job requirements. Select a focus for your cover letter:");

        setPendingInput({
          question: "Select Cover Letter Focus",
          suggestions: [
            {
              label: "Focus on React & UI Skills",
              action: () => generateCoverLetter("I have extensive experience in building responsive UI and component libraries using React and TailwindCSS.")
            },
            {
              label: "Focus on Full Stack & Scalability",
              action: () => generateCoverLetter("I specialize in building scalable backend services with Node.js while maintaining a seamless frontend experience.")
            },
            {
              label: "Focus on Passion for Product",
              action: () => generateCoverLetter("I am deeply passionate about user-centric product design and have followed your company's work for years.")
            }
          ],
          onSubmit: (input) => generateCoverLetter(input) // Fallback if they type
        });
      };
      fillData();
    }
  }, [isActive, agentState, user, updateAgentState, addLog, setPendingInput]);

  // 3. Cover Letter Generation (Fast)
  const generateCoverLetter = async (userInput) => {
    setPendingInput(null);
    setAgentMessage("Drafting cover letter...");

    const template = `Dear Hiring Manager,\n\nI am writing to express my strong interest in the ${job?.title || 'position'} at ${job?.company?.name || 'your company'}.\n\n${userInput}\n\nI am confident that my skills making me a valuable addition to your team.\n\nSincerely,\n${user?.user_metadata?.first_name || 'Alex'} ${user?.user_metadata?.last_name || 'Chen'}`;

    await new Promise(r => setTimeout(r, 1000));
    setFormData(prev => ({ ...prev, coverLetter: template }));
    addLog("Generated cover letter.", 'success');

    // Auto move to questions
    setCurrentStep(3);
    attemptFinalQuestions();
  };

  // 4. Verification & Final Questions (Buttons)
  const processFinalQuestions = async (currentAnswers = formData.answers) => {
    const newAnswers = { ...currentAnswers };
    let nextQuestionToAsk = null;

    job?.applicationQuestions?.forEach(q => {
      if (newAnswers[q.id]) return;

      // Heuristic matching 
      if (q.question.toLowerCase().includes('experience')) {
        newAnswers[q.id] = "5 years of full-stack development experience with React/Node.";
        addLog(`Auto-answered "${q.question}"`, 'success');
      } else {
        if (!nextQuestionToAsk) nextQuestionToAsk = q;
      }
    });

    setFormData(prev => ({ ...prev, answers: newAnswers }));

    if (nextQuestionToAsk) {
      updateAgentState('input_needed', `Suggestion for "${nextQuestionToAsk.question}":`);

      setPendingInput({
        question: nextQuestionToAsk.question,
        suggestions: [
          { label: "Yes, I have experience.", action: () => submitAnswer("Yes, I have relevant experience.") },
          { label: "No, but I'm learning.", action: () => submitAnswer("I am currently learning this technology.") },
          { label: "Use Resume Answer", action: () => submitAnswer("See resume for details.") }
        ],
        onSubmit: (val) => submitAnswer(val)
      });

      const submitAnswer = (val) => {
        const updated = { ...newAnswers, [nextQuestionToAsk.id]: val };
        addLog(`Recorded: ${val}`, 'success');
        setPendingInput(null);
        processFinalQuestions(updated);
      };

    } else {
      // All done!
      updateAgentState('review', "Application ready! Click submit to finish.");
      setPendingInput({
        question: "Confirm Submission",
        suggestions: [
          { label: "Submit Application", action: () => handleSubmit({ preventDefault: () => { } }) },
          { label: "Review First", action: () => setAgentMessage("Okay, review the form and click 'Submit Application' when ready.") }
        ],
        onSubmit: (input) => {
          if (input.toLowerCase().includes('submit')) handleSubmit({ preventDefault: () => { } });
        }
      });
    }
  };

  const attemptFinalQuestions = async () => {
    setAgentMessage("Scanning resume for answers to additional questions...");
    await new Promise(r => setTimeout(r, 1500));
    processFinalQuestions(formData.answers);
  };

  // --- END AI AGENT LOGIC ---

  const renderAgentView = () => {
    return (
      <div className="flex flex-col items-center justify-center p-12 text-center space-y-6 min-h-[400px]">
        <div className="relative">
          <div className="w-24 h-24 rounded-full bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center animate-pulse">
            <Icon name="Bot" size={48} className="text-violet-600 dark:text-violet-400" />
          </div>
          <div className="absolute -bottom-2 -right-2 w-8 h-8 bg-green-500 rounded-full border-4 border-white dark:border-neutral-900 flex items-center justify-center">
            <Icon name="Check" size={16} className="text-white" />
          </div>
        </div>

        <div className="max-w-md space-y-2">
          <h3 className="text-2xl font-bold text-text-primary">
            Agentic Mode Active
          </h3>
          <p className="text-text-muted">
            {agentState === 'analyzing' && "Analyzing job requirements..."}
            {agentState === 'filling' && "Auto-filling your profile information..."}
            {agentState === 'input_needed' && (
              <>
                <span className="block font-medium text-violet-600 dark:text-violet-400 mb-2">
                  {pendingInput?.question || "Action Required"}
                </span>
                Please check the <strong>Agent Chat</strong> (bottom right) to respond.
              </>
            )}
            {agentState === 'verifying' && "Verifying application details..."}
          </p>
        </div>

        {/* Visual Progress or Stats */}
        <div className="w-full max-w-xs bg-secondary-200 rounded-full h-1.5 mt-8 overflow-hidden">
          {agentState === 'analyzing' && <div className="h-full bg-violet-500 w-1/4 animate-pulse"></div>}
          {agentState === 'filling' && <div className="h-full bg-violet-500 w-2/4 transition-all duration-1000"></div>}
          {agentState === 'input_needed' && <div className="h-full bg-violet-500 w-3/4 transition-all duration-1000"></div>}
          {agentState === 'verifying' && <div className="h-full bg-violet-500 w-full transition-all duration-1000"></div>}
        </div>
      </div>
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateStep(currentStep)) {
      return;
    }

    if (!user) {
      alert('You must be logged in to apply.');
      return;
    }

    setIsSubmitting(true);

    try {
      let resumeUrl = null;

      // Upload resume if present AND it's a new file (not just metadata)
      if (resumeFile) {
        const fileExt = resumeFile.name.split('.').pop();
        const fileName = `${user.id}/${Date.now()}.${fileExt}`;
        const filePath = `${fileName}`;

        const { error: uploadError } = await supabase.storage
          .from('resumes')
          .upload(filePath, resumeFile);

        if (uploadError) throw uploadError;

        const { data: { publicUrl } } = supabase.storage
          .from('resumes')
          .getPublicUrl(filePath);

        resumeUrl = publicUrl;
      }

      // Submit application
      const { error } = await supabase
        .from('job_applications')
        .insert({
          user_id: user.id,
          job_id: job.id,
          resume_url: resumeUrl,
          status: 'pending'
        });

      if (error) {
        if (error.code === '23505') {
          alert('You have already applied for this job.');
        } else {
          throw error;
        }
      } else {
        if (isActive) {
          updateAgentState('complete', `Application submitted successfully to ${job.company?.name}! Tracking in your dashboard.`);
          addLog("Application submitted.", 'success');
          setPendingInput(null);
        } else {
          alert('Application submitted successfully!');
        }
        onClose();
      }
    } catch (error) {
      console.error('Submission error:', error.message);
      alert('Failed to submit application: ' + error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-text-primary mb-4">Personal Information</h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  First Name *
                </label>
                <input
                  type="text"
                  value={formData.firstName}
                  onChange={(e) => handleInputChange('firstName', e.target.value)}
                  className={`input-field ${errors.firstName ? 'border-error' : ''}`}
                  placeholder="Enter your first name"
                />
                {errors.firstName && (
                  <p className="text-error text-sm mt-1">{errors.firstName}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  Last Name *
                </label>
                <input
                  type="text"
                  value={formData.lastName}
                  onChange={(e) => handleInputChange('lastName', e.target.value)}
                  className={`input-field ${errors.lastName ? 'border-error' : ''}`}
                  placeholder="Enter your last name"
                />
                {errors.lastName && (
                  <p className="text-error text-sm mt-1">{errors.lastName}</p>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Email Address *
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                className={`input-field ${errors.email ? 'border-error' : ''}`}
                placeholder="Enter your email address"
              />
              {errors.email && (
                <p className="text-error text-sm mt-1">{errors.email}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Phone Number *
              </label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                className={`input-field ${errors.phone ? 'border-error' : ''}`}
                placeholder="Enter your phone number"
              />
              {errors.phone && (
                <p className="text-error text-sm mt-1">{errors.phone}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                LinkedIn Profile (Optional)
              </label>
              <input
                type="url"
                value={formData.linkedinUrl}
                onChange={(e) => handleInputChange('linkedinUrl', e.target.value)}
                className="input-field"
                placeholder="https://linkedin.com/in/yourprofile"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Portfolio/Website (Optional)
              </label>
              <input
                type="url"
                value={formData.portfolioUrl}
                onChange={(e) => handleInputChange('portfolioUrl', e.target.value)}
                className="input-field"
                placeholder="https://yourportfolio.com"
              />
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-text-primary mb-4">Documents</h3>

            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Resume/CV *
              </label>
              <div className="border-2 border-dashed border-border rounded-lg p-6 text-center">
                <input
                  type="file"
                  accept=".pdf,.doc,.docx"
                  onChange={handleFileUpload}
                  className="hidden"
                  id="resume-upload"
                />
                <label htmlFor="resume-upload" className="cursor-pointer">
                  <div className="flex flex-col items-center space-y-2">
                    <Icon name="Upload" size={32} className="text-secondary-400" />
                    <div>
                      <p className="text-text-primary font-medium">
                        {formData.resume ? formData.resume.name : 'Click to upload resume'}
                      </p>
                      <p className="text-text-muted text-sm">PDF, DOC, or DOCX (max 5MB)</p>
                    </div>
                  </div>
                </label>
              </div>
              {errors.resume && (
                <p className="text-error text-sm mt-1">{errors.resume}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Cover Letter *
              </label>
              <textarea
                value={formData.coverLetter}
                onChange={(e) => handleInputChange('coverLetter', e.target.value)}
                rows={6}
                className={`input-field resize-none ${errors.coverLetter ? 'border-error' : ''}`}
                placeholder="Tell us why you're interested in this position and what makes you a great fit..."
              />
              <div className="flex justify-between items-center mt-1">
                {errors.coverLetter && (
                  <p className="text-error text-sm">{errors.coverLetter}</p>
                )}
                <p className="text-text-muted text-sm ml-auto">
                  {formData.coverLetter.length}/1000 characters
                </p>
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-text-primary mb-4">Additional Questions</h3>

            {job.applicationQuestions.map((question) => (
              <div key={question.id}>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  {question.question} {question.required && '*'}
                </label>

                {question.type === 'textarea' && (
                  <textarea
                    value={formData.answers[question.id] || ''}
                    onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                    rows={4}
                    className={`input-field resize-none ${errors[`question_${question.id}`] ? 'border-error' : ''}`}
                    placeholder="Enter your answer..."
                  />
                )}

                {question.type === 'radio' && (
                  <div className="space-y-2">
                    {question.options.map((option) => (
                      <label key={option} className="flex items-center space-x-2">
                        <input
                          type="radio"
                          name={`question_${question.id}`}
                          value={option}
                          checked={formData.answers[question.id] === option}
                          onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                          className="text-primary-600 focus:ring-primary-500"
                        />
                        <span className="text-text-secondary">{option}</span>
                      </label>
                    ))}
                  </div>
                )}

                {errors[`question_${question.id}`] && (
                  <p className="text-error text-sm mt-1">{errors[`question_${question.id}`]}</p>
                )}
              </div>
            ))}
          </div>
        );

      default:
        return null;
    }
  };

  if (isMinimized) {
    return (
      <div className="fixed bottom-6 left-6 z-50 animate-in slide-in-from-bottom-5 fade-in duration-300">
        <PulseBeamBorder className="rounded-full bg-white dark:bg-neutral-900 shadow-xl">
          <div className="px-4 py-3 flex items-center gap-3">
            <div className="relative">
              <div className="w-8 h-8 rounded-full bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center animate-pulse">
                <Icon name="Bot" size={16} className="text-violet-600 dark:text-violet-400" />
              </div>
              <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-green-500 rounded-full border-2 border-white dark:border-neutral-900"></div>
            </div>
            <div className="flex flex-col">
              <span className="text-xs font-bold text-text-primary">Agent Active</span>
              <span className="text-[10px] text-text-muted max-w-[150px] truncate">
                {agentState === 'input_needed' ? 'Waiting for input...' : 'Working in background...'}
              </span>
            </div>
            <button
              onClick={() => setIsMinimized(false)}
              className="p-1.5 hover:bg-neutral-100 dark:hover:bg-neutral-800 rounded-full transition-colors ml-2"
              title="Restore Form"
            >
              <Icon name="Maximize2" size={14} className="text-text-secondary" />
            </button>
          </div>
        </PulseBeamBorder>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-1050 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="bg-background rounded-lg shadow-modal max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-xl font-semibold text-text-primary">Apply for {job.title}</h2>
            <p className="text-text-muted text-sm">{job.company.name}</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setIsMinimized(true)}
              className="p-2 hover:bg-surface rounded-lg transition-smooth text-text-secondary"
              title="Minimize"
            >
              <Icon name="Minimize2" size={20} />
            </button>
            <button
              onClick={onClose}
              className="p-2 hover:bg-surface rounded-lg transition-smooth text-text-secondary"
            >
              <Icon name="X" size={20} />
            </button>
          </div>
        </div>

        {/* Progress Bar - Hide in Agent Mode */}
        {(!isActive || agentState === 'review') && (
          <div className="px-6 py-4 border-b border-border">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-text-primary">
                Step {currentStep} of {totalSteps}
              </span>
              <span className="text-sm text-text-muted">
                {Math.round((currentStep / totalSteps) * 100)}% Complete
              </span>
            </div>
            <div className="w-full bg-secondary-200 rounded-full h-2">
              <div
                className="bg-primary-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${(currentStep / totalSteps) * 100}%` }}
              ></div>
            </div>
          </div>
        )}

        {/* Form Content */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {isActive && agentState !== 'review' && agentState !== 'complete' ? (
            renderAgentView()
          ) : (
            <form onSubmit={handleSubmit}>
              {renderStepContent()}
            </form>
          )}
        </div>

        {/* Footer - Hide if Agent is driving (except review) */}
        {(!isActive || agentState === 'review') && (
          <div className="flex items-center justify-between p-6 border-t border-border">
            <button
              type="button"
              onClick={handlePrevious}
              disabled={currentStep === 1}
              className="btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Icon name="ChevronLeft" size={16} className="mr-1" />
              Previous
            </button>

            <div className="flex space-x-3">
              {currentStep < totalSteps ? (
                <button
                  type="button"
                  onClick={handleNext}
                  className="btn-primary"
                >
                  Next
                  <Icon name="ChevronRight" size={16} className="ml-1" />
                </button>
              ) : (
                <button
                  type="submit"
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <>
                      <Icon name="Loader2" size={16} className="mr-2 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Icon name="Send" size={16} className="mr-2" />
                      Submit Application
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default JobApplicationForm;